import React, { useMemo } from 'react';

import { COLUMN_CATEGORY_ORDER } from '@aline/common';
import { Typography } from '@mui/material';
import {
  gridFilterModelSelector,
  useGridApiContext,
  useGridSelector,
} from '@mui/x-data-grid-premium';

import { FilterPopoverContent } from './FilterPopoverContent';
import type { FilterContentProps } from './types';
import { ClickableColumnIcon } from '../../board/settings/ColumnItem';
import { getCategoryLabel } from '../../board/settings/helpers';

export const STATUS_OPTIONS = COLUMN_CATEGORY_ORDER.map(category => ({
  category,
  id: category,
  label: getCategoryLabel(category),
}));

export function StatusFilterContent({ field, operator }: FilterContentProps): JSX.Element {
  const apiRef = useGridApiContext();
  const filterModel = useGridSelector(apiRef, gridFilterModelSelector);

  const selectedIds = useMemo(() => {
    const filterItem = filterModel.items.find(item => item.field === field);
    const rawValues = filterItem?.value ?? [];
    return new Set(Array.isArray(rawValues) ? rawValues.map(String) : []);
  }, [filterModel, field]);

  const handleToggle = (categoryId: string) => {
    const currentValues = filterModel.items.find(item => item.field === field)?.value ?? [];
    const isSelected = selectedIds.has(categoryId);

    const newValues = isSelected
      ? currentValues.filter((v: string) => String(v) !== categoryId)
      : [...currentValues, categoryId];

    const updatedItems = filterModel.items.filter(item => item.field !== field);

    if (newValues.length > 0) {
      updatedItems.push({ field, operator, value: newValues });
    }

    apiRef.current.setFilterModel({ ...filterModel, items: updatedItems });
  };

  return (
    <FilterPopoverContent
      options={STATUS_OPTIONS}
      renderOption={option => (
        <>
          <ClickableColumnIcon
            category={option.category}
            color={null}
            iconSize={16}
            showBackground={false}
            showBorder={false}
          />
          <Typography variant="body2">{option.label}</Typography>
        </>
      )}
      selectedIds={selectedIds}
      onToggle={handleToggle}
    />
  );
}
